'use client';

import { useState, type ReactNode } from 'react';
import { ChevronDown, Check, AlertCircle, Lightbulb, type LucideIcon } from 'lucide-react';

export const WIZARD_MAX_WIDTH = 'max-w-none';

export const WIZARD_PANEL_SHADOW = '';

export const WIZARD_PANEL_RING = '';

export const inputClass = 'ew-input';

export const textareaClass = 'ew-input ew-textarea';

export const textareaLgClass = 'ew-input ew-textarea min-h-[10.5rem]';

export function WizardCard({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`ew-panel ${className}`}>{children}</div>;
}

/** Unified wizard section shell */
export function WizardPanelCard({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <WizardCard className={`p-0 ${className}`}>{children}</WizardCard>;
}

export function WizardPanelHeader({
  title,
  description,
  action,
  embedded,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  embedded?: boolean;
}) {
  if (embedded) {
    return (
      <div className="ew-step-head border-b border-[var(--color-border)] px-5 py-4 sm:px-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <StepHeader embedded title={title} description={description} />
          {action ? <div className="shrink-0">{action}</div> : null}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[var(--color-border)] px-5 py-4 sm:px-6">
      <SectionHeader title={title} description={description} />
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function WizardPanelBody({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`ew-panel-body ${className}`}>{children}</div>;
}

export function WizardSubsectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="mb-3 text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-[var(--color-text-muted)]">
      {children}
    </p>
  );
}

export function WizardPanelDivider() {
  return <div className="border-t border-nature-border/70 pt-8" />;
}

export function WizardProgressBar({
  current,
  total,
  labels,
}: {
  current: number;
  total: number;
  labels?: string[];
}) {
  const pct = total > 0 ? Math.round(((current + 1) / total) * 100) : 0;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 text-xs font-medium text-[var(--color-text-muted)]">
        <span>
          Step {current + 1} of {total}
          {labels?.[current] ? ` · ${labels[current]}` : ''}
        </span>
        <span>{pct}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-[var(--color-surface-muted)] border border-[var(--color-border)]">
        <div
          className="h-full rounded-full bg-[var(--color-primary)] transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function WizardPageHeader({
  title,
  subtitle,
  action,
  stepIndex,
  stepTotal,
  stepLabels,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  stepIndex?: number;
  stepTotal?: number;
  stepLabels?: string[];
}) {
  const hasSteps = typeof stepIndex === 'number' && typeof stepTotal === 'number';
  const currentLabel = hasSteps && stepLabels?.[stepIndex] ? stepLabels[stepIndex] : null;
  const stepLabel = hasSteps
    ? currentLabel
      ? `You're on ${currentLabel}. Fill this step, then continue.`
      : subtitle
    : subtitle;
  const pct = hasSteps ? Math.round(((stepIndex + 1) / stepTotal) * 100) : null;

  return (
    <header className="ew-hero">
      <div className="ew-hero-glow" aria-hidden />
      <div className="ew-hero-inner">
        <div className="min-w-0">
          <p className="ew-hero-kicker">Event setup</p>
          <h1 className="ew-hero-title">{title}</h1>
          {stepLabel ? <p className="ew-hero-step">{stepLabel}</p> : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      {hasSteps ? (
        <div className="ew-hero-stats">
          <span className="ew-hero-chip">
            <strong>
              {stepIndex + 1}/{stepTotal}
            </strong>{' '}
            step
          </span>
          {currentLabel ? (
            <span className="ew-hero-chip">
              <strong>{currentLabel}</strong>
            </span>
          ) : null}
          {pct != null ? (
            <span className="ew-hero-chip">
              <strong>{pct}%</strong> complete
            </span>
          ) : null}
        </div>
      ) : null}
    </header>
  );
}

export function StepHeader({
  title,
  description,
  embedded,
}: {
  title: string;
  description?: string;
  embedded?: boolean;
}) {
  if (embedded) {
    return (
      <div>
        <h2 className="ew-step-title">{title}</h2>
        {description ? <p className="ew-step-desc">{description}</p> : null}
      </div>
    );
  }

  return (
    <div className="ew-panel p-5 sm:px-6">
      <h2 className="ew-step-title">{title}</h2>
      {description ? <p className="ew-step-desc">{description}</p> : null}
    </div>
  );
}

export function WizardHelpBanner({
  icon: Icon = Lightbulb,
  title,
  children,
}: {
  icon?: LucideIcon;
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className="ew-tip">
      <Icon className="h-4 w-4" />
      <div>
        {title ? <strong className="font-semibold text-[var(--color-text)]">{title}: </strong> : null}
        {children}
      </div>
    </div>
  );
}

export function WizardFormSection({
  title,
  description,
  children,
  divider,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  divider?: boolean;
}) {
  return (
    <section className={divider ? 'ew-section border-t border-[var(--color-border)] pt-6' : 'ew-section'}>
      <SectionHeader title={title} description={description} />
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
}

export function SectionHeader({ title, description }: { title: string; description?: string }) {
  return (
    <div>
      <h3 className="ew-section-title">{title}</h3>
      {description ? <p className="ew-section-desc">{description}</p> : null}
    </div>
  );
}

export function Field({
  label,
  required,
  error,
  hint,
  children,
}: {
  label: ReactNode;
  required?: boolean;
  error?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="ew-field">
      <label className="ew-field-label">
        {label} {required ? <span className="font-normal text-[var(--color-text-muted)]">*</span> : null}
      </label>
      {children}
      {hint && !error ? <p className="ew-field-hint">{hint}</p> : null}
      {error ? <p className="ew-field-error">{error}</p> : null}
    </div>
  );
}

export function CollapsibleSection({
  title,
  description,
  defaultOpen = true,
  children,
}: {
  title: string;
  description?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="ew-collapse">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="ew-collapse-trigger"
        aria-expanded={open}
      >
        <div>
          <p className="ew-collapse-title">{title}</p>
          {description ? <p className="ew-collapse-desc">{description}</p> : null}
        </div>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-[var(--color-text-muted)] transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open ? <div className="ew-collapse-body">{children}</div> : null}
    </div>
  );
}

export function CompletionBadge({ complete, label }: { complete: boolean; label?: string }) {
  return (
    <span className={`ew-badge ${complete ? 'ew-badge--ok' : 'ew-badge--warn'}`}>
      {complete ? <Check className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
      {label ?? (complete ? 'Complete' : 'Incomplete')}
    </span>
  );
}

export function ChecklistRow({ label, complete }: { label: string; complete: boolean }) {
  return (
    <li className="ew-check-row">
      <span className={`ew-check-dot ${complete ? 'ew-check-dot--done' : ''}`}>
        {complete ? <Check className="h-3.5 w-3.5" /> : null}
      </span>
      <span className={`text-sm ${complete ? 'font-medium text-[var(--color-text)]' : 'text-[var(--color-text-muted)]'}`}>
        {label}
      </span>
      <span className="ml-auto">
        <CompletionBadge complete={complete} label={complete ? 'Done' : 'Missing'} />
      </span>
    </li>
  );
}

export function ReadyBadge({ ready }: { ready: boolean }) {
  return (
    <div className={`ew-badge ${ready ? 'ew-badge--ok' : 'ew-badge--warn'}`} style={{ padding: '0.45rem 0.85rem', fontSize: '0.8125rem' }}>
      {ready ? <Check className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
      {ready ? 'Ready to publish' : 'Not ready to publish'}
    </div>
  );
}
